import React, { useCallback } from "react";
import { sendClientSideSplitTrackEvent } from "@leafly-com/split-next";
import classNames from "classnames";
import Link from "next/link";

import { CATEGORY_NAMES } from "constants/dispensary";
import { Action, Category } from "constants/events";
import { useEventTracker } from "hooks/useEventTracker";
import useFilters from "hooks/useFilters";
import isMobileDevice from "utils/isMobileDevice";
import { getSplitKey } from "utils/split/getSplitKey";

import ProductCategoryCard from "components/botanic/ProductCategoryCard";
import ProductCategoryCardPlaceholder from "components/botanic/ProductCategoryCard/ProductCategoryCardPlaceholder";
import IconSvg from "components/IconSvg";

type Props = {
  className?: string;
  isLoading?: boolean;
  coins: string[];
  hrefLink?: string;
  hrefLinkQueryParam?: string;
  hrefProductCatQueryKey?: string;
  title?: string;
  trackEvent?: {
    eventAction?: string;
    eventCategory?: string;
  };
  filterable?: boolean;
  showNewCoinListExperience?: boolean;
  tab?: string;
};

const CoinList: React.FC<Props> = ({
  className,
  coins,
  isLoading = false,
  hrefLink = "/search/shop",
  hrefLinkQueryParam = "",
  hrefProductCatQueryKey = "product_category",
  title,
  trackEvent,
  filterable,
  showNewCoinListExperience,
  tab,
}) => {
  const { toggleFilter } = useFilters({
    shallowRouting: true,
  });
  const onFilterClick = useCallback(
    (filterOption: string) => {
      toggleFilter({ [`product_category[]`]: filterOption });
    },
    [toggleFilter]
  );
  const { publishEvent } = useEventTracker();
  const isMobile = isMobileDevice();

  const publishEventAction = trackEvent?.eventAction || Action.click;
  const publishEventCategory =
    trackEvent?.eventCategory || Category.productCategoryCoinFilter;

  const coinCategories = Object.keys(CATEGORY_NAMES).filter((category) =>
    coins.includes(category)
  );

  const handleSplitEventTracking = () => {
    sendClientSideSplitTrackEvent(
      {
        eventType: "nl-894_coin_list_menu_conversion",
        properties: { isMobile, menuConversion: !filterable },
      },
      getSplitKey()
    );
    sendClientSideSplitTrackEvent(
      {
        eventType: "nl-894_coin_list_click",
        properties: { click: true, isMobile, ...(tab ? { tab } : {}) },
      },
      getSplitKey()
    );
  };

  return isLoading || coinCategories.length ? (
    <div className={className}>
      {title && <h2 className="heading--m font-bold">{title}</h2>}
      <div className="overflow-x-auto">
        {!isLoading && coinCategories.length ? (
          <ul
            data-testid="coin-list"
            className={classNames("flex", {
              "gap-lg": showNewCoinListExperience,
            })}
          >
            {coinCategories.map((category) =>
              showNewCoinListExperience ? (
                <Coin
                  key={category}
                  href={`${hrefLink}?${hrefProductCatQueryKey}=${category}${hrefLinkQueryParam}`}
                  onClick={() => {
                    publishEvent({
                      action: publishEventAction,
                      category: publishEventCategory,
                      label: CATEGORY_NAMES[category]
                        .toLowerCase()
                        .replace(/-/g, ""),
                    });
                    handleSplitEventTracking();
                    filterable && onFilterClick(category);
                  }}
                  title={CATEGORY_NAMES[category].toLowerCase()}
                  filterable={filterable}
                  category={category}
                />
              ) : (
                <li
                  key={category}
                  className="mx-sm flex-shrink-0"
                  style={{ minWidth: 80, width: 80 }}
                >
                  <ProductCategoryCard
                    image={`https://leafly-public.imgix.net/web-dispensary/category-images/${category.toLowerCase()}_300.png`}
                    title={CATEGORY_NAMES[category]}
                    titleSize="xs"
                    titleClassName="underline"
                    url={`${hrefLink}?${hrefProductCatQueryKey}=${category}${hrefLinkQueryParam}`}
                    disableLazyLoad={true}
                    sizes={[80]}
                    onClick={() => {
                      publishEvent({
                        action: publishEventAction,
                        category: publishEventCategory,
                        label: CATEGORY_NAMES[category]
                          .toLowerCase()
                          .replace(/-/g, ""),
                      });
                      handleSplitEventTracking();
                      filterable && onFilterClick(category);
                    }}
                    filterable={filterable}
                  />
                </li>
              )
            )}
          </ul>
        ) : (
          isLoading && (
            <div className="flex">
              {Array(10)
                .fill(null)
                .map((_, i) => (
                  <ProductCategoryCardPlaceholder
                    key={`coin-list-${i}`}
                    className="mx-sm"
                    style={{ minWidth: 80, width: 80 }}
                  />
                ))}
            </div>
          )
        )}
      </div>
    </div>
  ) : (
    <></>
  );
};

const Coin = ({
  category,
  filterable,
  onClick,
  href,
  title,
}: {
  category: string;
  onClick: () => void;
  href: string;
  filterable?: boolean;
  title: string;
}) => {
  return (
    <li
      key={category}
      className="flex-shrink-0 mb-9"
      style={{ minWidth: 80, width: 80 }}
      data-testid={`${title}-products-category`}
    >
      <Link
        href={filterable ? "" : href}
        onClick={onClick}
        className="flex border border-grey-44 flex-col items-center justify-center h-[80px] bg-green-20 rounded-[8px]"
      >
        <IconSvg
          className={`flex items-center text-white ${
            category === "Flower" && "pt-1"
          }`}
          width={category === "Flower" ? "44" : "50"}
          height={category === "Flower" ? "42" : "50"}
          filePath={`coinList/${category.toLowerCase()}.svg`}
        />
        <span className="text-xs">{title}</span>
      </Link>
    </li>
  );
};

export default CoinList;
